import React, { useState, useEffect } from 'react';
import { createTherapist, updateTherapist, getLevels, getNationalities } from '../../../../APICalling/APICalling';
import { toast } from 'react-toastify';
import EditIcon from '../../../../Components/Icons/EditIcon';
import InfoIcon from '../../../../Components/Icons/InfoIcon';
import CustomDropdown from '../../../../Components/CustomerDetailsModal/CustomDropdown';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import styles from './AddEditTherapist.module.css';
import "react-phone-number-input/style.css";
import { getCountryCallingCode, parsePhoneNumber, getCountries, isValidPhoneNumber } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import en from "react-phone-number-input/locale/en.json";
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import CountrySelectorWithSearch from '../../../../Components/PhoneInput/CountrySelectorWithSearch';
import logger from '../../../../Utils/logger';

// Module-level cache for employee categories and nationalities (persists across component re-renders)
// Employee categories are outlet-specific, so cache by outlet id.
let cachedEmployeeCategoriesByOutlet = {};
let cachedNationalities = null;

const AddEditTherapist = ({ therapist, mode = 'show', onSave, onCancel }) => {
    // mode can be: 'show', 'edit', 'create'
    const { selectedOutlet, loggedOutletID, setHomeStaffList } = useGlobalContext();
    const currentOutletId = selectedOutlet || loggedOutletID;
    
    // Get placeholder based on selected country using libphonenumber-js
    const getMobilePlaceholder = (countryCode) => {
        try {
            // Get example number for the country
            const exampleNumber = getExampleNumber(countryCode, examples);
            if (exampleNumber) {
                // Format the example number and remove country code
                const formatted = exampleNumber.formatNational();
                return formatted || 'Mobile number';
            }
        } catch (error) {
            logger.error('Error getting example number:', error);
        }
        return 'Mobile number';
    };

    const [formData, setFormData] = useState({
        name: '',
        status: 1,
        employee_category: '',
        gender: '',
        pager_number: '',
        infotech_code: ''
    });

    const [isEditing, setIsEditing] = useState(mode === 'edit' || mode === 'create');
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [employeeCategories, setEmployeeCategories] = useState(() => {
        if (!currentOutletId) return [];
        return cachedEmployeeCategoriesByOutlet?.[String(currentOutletId)] || [];
    });
    const [nationalities, setNationalities] = useState(cachedNationalities || []);
    const [selectedCountry, setSelectedCountry] = useState('SG');
    const [displayPhoneNumber, setDisplayPhoneNumber] = useState('');

    // Fetch employee categories (per outlet) and nationalities (global) from API.
    useEffect(() => {
        // Employee categories depend on outlet; fetch when outlet changes (or read from cache).
        if (currentOutletId) {
            const cacheKey = String(currentOutletId);
            const cachedForOutlet = cachedEmployeeCategoriesByOutlet?.[cacheKey];
            if (cachedForOutlet && Array.isArray(cachedForOutlet) && cachedForOutlet.length > 0) {
                setEmployeeCategories(cachedForOutlet);
            } else {
                fetchEmployeeCategories(currentOutletId);
            }
        }

        if (!cachedNationalities) {
            fetchNationalities();
        } else {
            // If cached, ensure state is updated
            setNationalities(cachedNationalities);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOutletId]);

    const fetchEmployeeCategories = async (outletIdParam) => {
        const outletIdToUse = outletIdParam || selectedOutlet || loggedOutletID;
        if (!outletIdToUse) return;
        try {
            const response = await getLevels({ 
                pagination: 0,
                status: 1,
                outlet: outletIdToUse
            });
            
            if (response?.data?.success) {
                // Access the category array from the nested structure
                const levels = response.data.data?.list?.category || [];
                // Map levels to dropdown format
                const mappedLevels = levels.map(level => ({
                    value: level.id?.toString(),
                    label: level.name || level.title
                }));
                
                // Cache at module level (per outlet)
                const cacheKey = String(outletIdToUse);
                cachedEmployeeCategoriesByOutlet = {
                    ...(cachedEmployeeCategoriesByOutlet || {}),
                    [cacheKey]: mappedLevels
                };
                setEmployeeCategories(mappedLevels);
            }
        } catch (error) {
            console.error('Error fetching employee categories:', error);
            toast.error('Failed to load employee categories');
        }
    };

    const fetchNationalities = async () => {
        try {
            const response = await getNationalities({ 
                pagination: 0
            });
            
            // console.log('Nationalities API Response:', response);
            
            if (response?.data?.success && response?.data?.data?.list?.nationalities) {
                // Access the nationalities array from the response structure
                const nationalitiesList = response.data.data.list.nationalities || [];
                // Map nationalities to dropdown format
                const mappedNationalities = nationalitiesList
                    .filter(nationality => nationality.status === 1) // Only active nationalities
                    .map(nationality => ({
                        value: nationality.id?.toString(),
                        label: nationality.name
                    }));
                
                // console.log('Mapped Nationalities:', mappedNationalities);
                
                // Cache at module level
                cachedNationalities = mappedNationalities;
                setNationalities(mappedNationalities);
            }
        } catch (error) {
            console.error('Error fetching nationalities:', error);
            toast.error('Failed to load nationalities');
        }
    };

    useEffect(() => {
        if (therapist) {
            // Map the API response to form data
            // API returns: alias, code, level_id, level_name, nationality_id, etc.
            const mappedData = {
                name: therapist.alias || therapist.name || '',
                status: therapist.status !== undefined ? therapist.status : 1,
                employee_category: therapist.level_id?.toString() || '',
                gender: therapist.gender || '',
                pager_number: therapist.pager_number || '',
                infotech_code: therapist.code || therapist.infotech_code || ''
            };
            // console.log('Loading therapist data:', { 
            //     nationality_id_from_backend: therapist.nationality_id,
            //     mapped_nationality_id: mappedData.nationality_id,
            //     therapist
            // });
            setFormData(mappedData);
            
            // Extract country from phone number if available and set display number
            // if (therapist.contact_number) {
            //     try {
            //         const phoneNumber = parsePhoneNumber(therapist.contact_number);
            //         if (phoneNumber) {
            //             if (phoneNumber.country) {
            //                 setSelectedCountry(phoneNumber.country);
            //             }
            //             // Update display number to show only national number
            //             if (phoneNumber.nationalNumber) {
            //                 setDisplayPhoneNumber(phoneNumber.nationalNumber);
            //             }
            //         }
            //     } catch (e) {
            //         // If parsing fails, set default
            //         setDisplayPhoneNumber('');
            //     }
            // } else {
            //     setDisplayPhoneNumber('');
            // }
        } else {
            setDisplayPhoneNumber('');
        }
        setIsEditing(mode === 'edit' || mode === 'create');
    }, [therapist, mode]);


    const handleChange = (field, value) => {
        // COMMENTED: Status validation for active bookings
        // // Check if trying to set status to inactive when therapist has active bookings
        // if (field === 'status' && value === 0 && therapist?.has_active_booking) {
        //     setErrors(prev => ({
        //         ...prev,
        //         status: 'Cannot inactive therapist with active bookings'
        //     }));
        //     return; // Don't update the status
        // }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Validate mandatory fields
        if (!formData.name || formData.name.trim() === '') {
            newErrors.name = 'Work Name is required';
        }
        
        if (!formData.pager_number || formData.pager_number.trim() === '') {
            newErrors.pager_number = 'Employee Pager Number is required';
        } else {
            // Validate pager number is numeric and 1-4 digits
            if (!/^\d+$/.test(formData.pager_number.trim())) {
                newErrors.pager_number = 'The Pager Number must be a number';
            } else if (formData.pager_number.trim().length < 1 || formData.pager_number.trim().length > 4) {
                newErrors.pager_number = 'The pager number field must be between 1 and 4 digits';
            }
        }
        
        // if (!formData.infotech_code || formData.infotech_code.trim() === '') {
        //     newErrors.infotech_code = 'Infotech Code / Code is required';
        // }
        
        if (!formData.gender || formData.gender.trim() === '') {
            newErrors.gender = 'Gender is required';
        }

        // Therapist category is required while editing (both create & edit flows)
        if (isEditing) {
            if (!formData.employee_category || String(formData.employee_category).trim() === '') {
                newErrors.employee_category = 'Therapist category is required';
            }
        }
        
        // COMMENTED: Status validation
        // if (formData.status === undefined || formData.status === null) {
        //     newErrors.status = 'Status is required';
        // }
        // 
        // // Check if trying to deactivate therapist with active bookings
        // if (formData.status === 0 && therapist?.has_active_booking) {
        //     newErrors.status = 'Cannot inactive therapist with active bookings';
        // }
        
        // Validate mobile number if provided
        // Skip validation if Singapore (SG) is selected else validate
        // if (selectedCountry !== 'SG' && formData.contact_number && formData.contact_number.trim() !== '') {
        //     // Use isValidPhoneNumber from react-phone-number-input for validation
        //     if (!isValidPhoneNumber(formData.contact_number)) {
        //         newErrors.contact_number = 'Invalid phone number';
        //     }
        // }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveClick = async () => {
        // Validate required fields
        if (!validateForm()) {
            toast.error('Please fill all required fields');
            return;
        }

        // Check for outlet_id - prioritize selectedOutlet over loggedOutletID
        const outletId = selectedOutlet || loggedOutletID || therapist?.outlet_id;
        if (!outletId) {
            toast.error('Outlet ID is missing. Please select an outlet.');
            return;
        }

        setIsSaving(true);
        try {
            // Get nationality name from the selected nationality_id
            // const selectedNationality = nationalities.find(n => n.value === formData.nationality_id);
            // const nationalityName = selectedNationality?.label || '';

            // Prepare payload with outlet_id and level_id
            const payload = {
                alias: formData.name, // Backend expects 'alias'
                code: formData.infotech_code, // Backend expects 'code'
                level_id: formData.employee_category, // Backend expects 'level_id'
                outlet_id: outletId, // Include outlet_id
                status: formData.status,
                gender: formData.gender,
                pager_number: formData.pager_number,
                panel:"outlet",
                // contact_number: formData.contact_number,
                // nationality_id: formData.nationality_id,
                // nationality_name: nationalityName,
                // passport_number: formData.passport_number,
                // license_number: formData.license_number,
            };

            // console.log('Therapist Payload:', payload);

            let response;
            if (mode === 'create') {
                // Create new therapist
                response = await createTherapist(payload);
            } else {
                // Update existing therapist
                response = await updateTherapist(therapist.id, payload);
            }

            if (response?.data?.success) {
                toast.success(mode === 'create' ? 'Therapist created successfully!' : 'Therapist updated successfully!');
                setIsEditing(false);
                setErrors({});
                
                // Call parent callback with updated data
                if (onSave) {
                    onSave(response.data.data);
                }
                
            } else {
                // Check for errors at different possible locations
                const errorsObj = response?.data?.errors || response?.errors;
                
                // Handle backend validation errors
                if (errorsObj) {
                    const backendErrors = {};
                    Object.keys(errorsObj).forEach(key => {
                        const errorArray = errorsObj[key];
                        const errorMessage = Array.isArray(errorArray) ? errorArray[0] : errorArray;
                        
                        // Map backend field names to frontend field names
                        if (key === 'alias') {
                            backendErrors['name'] = errorMessage;
                        } else if (key === 'code') {
                            backendErrors['infotech_code'] = errorMessage;
                        } else {
                            backendErrors[key] = errorMessage;
                        }
                    });
                    
                    // console.log('Backend Errors Mapped:', backendErrors);
                    setErrors(backendErrors);
                    
                    // Show the first error in toast
                    const firstError = Object.values(backendErrors)[0];
                    toast.error(firstError || 'Failed to save therapist data');
                } else {
                    toast.error(response?.data?.message || 'Failed to save therapist data');
                }
            }
        } catch (error) {
            console.error('Error saving therapist:', error);
            toast.error('An error occurred while saving therapist data');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelClick = () => {
        // Clear all errors
        setErrors({});
        
        if (mode === 'create' && onCancel) {
            onCancel();
            // Reset form for create mode
            setDisplayPhoneNumber('');
            setSelectedCountry('SG');
        } else {
            setIsEditing(false);
            // Reset form data to original therapist data
            if (therapist) {
                setFormData({
                    name: therapist.alias || therapist.name || '',
                    // status: therapist.status !== undefined ? therapist.status : 1, // COMMENTED: Status field
                    employee_category: therapist.level_id?.toString() || '',
                    gender: therapist.gender || '',
                    pager_number: therapist.pager_number || '',
                    infotech_code: therapist.code || therapist.infotech_code || ''
                });
                
                // Reset display phone number and country from therapist data
                // if (therapist.contact_number) {
                //     try {
                //         const phoneNumber = parsePhoneNumber(therapist.contact_number);
                //         if (phoneNumber) {
                //             if (phoneNumber.country) {
                //                 setSelectedCountry(phoneNumber.country);
                //             }
                //             if (phoneNumber.nationalNumber) {
                //                 setDisplayPhoneNumber(phoneNumber.nationalNumber);
                //             }
                //         }
                //     } catch (e) {
                //         setDisplayPhoneNumber('');
                //     }
                // } else {
                //     setDisplayPhoneNumber('');
                // }
            }
        }
    };

    const genders = [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' }
    ];

    return (
        <div className={`${styles.addEditTherapist} ${mode === 'create' ? styles.createMode : ''}`}>
            <div className={styles.header}>
                <h3 className={styles.title}>
                    {mode === 'create' ? 'Add New Therapist ' : 'Details'}
                </h3>
                {!isEditing && mode !== 'create' && (
                    <button 
                        className={styles.editButton}
                        onClick={handleEditClick}
                    >
                        <span>Edit</span>
                        <EditIcon size={12} color="#3C2212" />
                    </button>
                )}
            </div>

            <div className={styles.formContent}>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Work Name*
                        </label>
                        {isEditing ? (
                            <>
                                <input
                                    type="text"
                                    className={`${styles.input} ${errors.name || errors.alias ? styles.inputError : ''}`}
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="Enter work name"
                                />
                                {(errors.name || errors.alias) && (
                                    <span className={styles.errorMessage}>
                                        {errors.name || errors.alias}
                                    </span>
                                )}
                            </>
                        ) : (
                            <div className={styles.value}>{formData.name || 'N/A'}</div>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Therapist Category*
                        </label>
                        {isEditing ? (
                            <>
                                <CustomDropdown
                                    options={employeeCategories}
                                    value={formData.employee_category}
                                    onChange={(value) => handleChange('employee_category', value)}
                                    placeholder="Select category"
                                    disableSearch={true}
                                    hideUnderline={false}
                                />
                                {errors.employee_category && (
                                    <span className={styles.errorMessage}>{errors.employee_category}</span>
                                )}
                            </>
                        ) : (
                            <div className={styles.value}>
                                {employeeCategories.find(c => c.value === formData.employee_category)?.label || 'N/A'}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.formRow}>
                    {/* <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Mobile Number
                        </label>
                        {isEditing ? (
                            <>
                                <div className={`${styles.phoneInputContainer} ${errors.contact_number ? styles.error : ''}`}>
                                    <div className={`${styles.phoneInputWrapper} hide-default-country-selector`}>
                                        <CountrySelectorWithSearch
                                            value={selectedCountry}
                                            onChange={(country) => {
                                                const countryCode = country?.code || country;
                                                setSelectedCountry(countryCode);
                                                // Update phone number with new country code if display number exists
                                                if (displayPhoneNumber) {
                                                    const digitsOnly = displayPhoneNumber.replace(/\D/g, '');
                                                    if (digitsOnly) {
                                                        const newNumber = `+${getCountryCallingCode(countryCode)}${digitsOnly}`;
                                                        handleChange('contact_number', newNumber);
                                                    }
                                                } else if (formData.contact_number) {
                                                    // Fallback: try to parse existing number
                                                    try {
                                                        const phoneNumber = parsePhoneNumber(formData.contact_number);
                                                        if (phoneNumber && phoneNumber.nationalNumber) {
                                                            const newNumber = `+${getCountryCallingCode(countryCode)}${phoneNumber.nationalNumber}`;
                                                            handleChange('contact_number', newNumber);
                                                            setDisplayPhoneNumber(phoneNumber.nationalNumber);
                                                        }
                                                    } catch (e) {
                                                        // If parsing fails, just update country
                                                    }
                                                }
                                            }}
                                            flags={flags}
                                            labels={en}
                                            disabled={isSaving}
                                        />
                                        <div className={styles.phoneInputOnly}>
                                            <input
                                                type="tel"
                                                value={displayPhoneNumber}
                                                onPaste={(e) => {
                                                    e.preventDefault();
                                                    const pastedText = e.clipboardData.getData('text');
                                                    
                                                    // Check if pasted text starts with + (international number)
                                                    if (pastedText.startsWith('+')) {
                                                        try {
                                                            const phoneNumber = parsePhoneNumber(pastedText);
                                                            if (phoneNumber && phoneNumber.country) {
                                                                // Valid international number with country
                                                                setSelectedCountry(phoneNumber.country);
                                                                setDisplayPhoneNumber(phoneNumber.nationalNumber || '');
                                                                handleChange('contact_number', pastedText);
                                                                return;
                                                            }
                                                        } catch (e) {
                                                            // If parsing fails, continue with normal flow
                                                        }
                                                    }
                                                    
                                                    // For non-international numbers, extract digits and format
                                                    const digitsOnly = pastedText.replace(/\D/g, '');
                                                    if (digitsOnly) {
                                                        const formattedValue = `+${getCountryCallingCode(selectedCountry)}${digitsOnly}`;
                                                        handleChange('contact_number', formattedValue);
                                                        setDisplayPhoneNumber(digitsOnly);
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const inputValue = e.target.value;

                                                    // If input is empty, clear everything
                                                    if (!inputValue) {
                                                        setDisplayPhoneNumber('');
                                                        handleChange('contact_number', '');
                                                        return;
                                                    }

                                                    // Check if user is typing an international number (starts with +)
                                                    if (inputValue.startsWith('+')) {
                                                        // Keep the + and digits as-is while typing
                                                        setDisplayPhoneNumber(inputValue);
                                                        
                                                        // Try to parse and auto-detect country (requires at least 4-5 digits after +)
                                                        const digitsOnly = inputValue.replace(/\D/g, '');
                                                        if (digitsOnly.length >= 4) {
                                                            try {
                                                                const phoneNumber = parsePhoneNumber(inputValue);
                                                                if (phoneNumber && phoneNumber.country) {
                                                                    // Valid international number with country - auto-detect country
                                                                    setSelectedCountry(phoneNumber.country);
                                                                    // Show only national number in input
                                                                    setDisplayPhoneNumber(phoneNumber.nationalNumber || digitsOnly);
                                                                    handleChange('contact_number', inputValue);
                                                                    return;
                                                                }
                                                            } catch (e) {
                                                                // Still parsing, continue typing
                                                            }
                                                        }
                                                        
                                                        // While typing international number (not yet valid), just store as-is
                                                        handleChange('contact_number', inputValue);
                                                        return;
                                                    }

                                                    // Regular input (no +) - format with selected country code
                                                    const digitsOnly = inputValue.replace(/\D/g, '');
                                                    setDisplayPhoneNumber(inputValue);
                                                    
                                                    if (!digitsOnly) {
                                                        handleChange('contact_number', '');
                                                        return;
                                                    }
                                                    
                                                    // Format with selected country code
                                                    const formattedValue = `+${getCountryCallingCode(selectedCountry)}${digitsOnly}`;
                                                    
                                                    handleChange('contact_number', formattedValue);
                                                }}
                                                placeholder={getMobilePlaceholder(selectedCountry)}
                                                className={styles.phoneInput}
                                                disabled={isSaving}
                                                autoComplete="tel"
                                                name="tel"
                                                style={{ paddingLeft: 0 }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {errors.contact_number && (
                                    <span className={styles.errorMessage}>{errors.contact_number}</span>
                                )}
                            </>
                        ) : (
                            <div className={styles.value}>{formData.contact_number || 'N/A'}</div>
                        )}
                    </div> */}

                    {/* <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Nationality
                        </label>
                        {isEditing ? (
                            <CustomDropdown
                                options={nationalities}
                                value={formData.nationality_id}
                                onChange={(value) => handleChange('nationality_id', value)}
                                placeholder="Select nationality"
                                disableSearch={false}
                                hideUnderline={false}
                            />
                        ) : (
                            <div className={styles.value}>
                                {(() => {
                                    const foundNationality = nationalities.find(n => n.value === formData.nationality_id?.toString())
                                    return foundNationality?.label || 'N/A';
                                })()}
                            </div>
                        )}
                    </div> */}

                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Gender*
                        </label>
                        {isEditing ? (
                            <>
                                <CustomDropdown
                                    options={genders}
                                    value={formData.gender}
                                    onChange={(value) => handleChange('gender', value)}
                                    placeholder="Select gender"
                                    disableSearch={true}
                                    hideUnderline={false}
                                />
                                {errors.gender && (
                                    <span className={styles.errorMessage}>{errors.gender}</span>
                                )}
                            </>
                        ) : (
                            <div className={styles.value}>
                                {genders.find(g => g.value === formData.gender)?.label || 'N/A'}
                            </div>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Therapist Pager Number*
                        </label>
                        {isEditing ? (
                            <>
                                <input
                                    type="text"
                                    className={`${styles.input} ${errors.pager_number ? styles.inputError : ''}`}
                                    value={formData.pager_number}
                                    onChange={(e) => {
                                        // Only allow numeric characters and limit to 4 digits
                                        const numericValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                                        handleChange('pager_number', numericValue);
                                    }}
                                    placeholder="Enter pager number (1-4 digits)"
                                    maxLength="4"
                                />
                                {errors.pager_number && (
                                    <span className={styles.errorMessage}>{errors.pager_number}</span>
                                )}
                            </>
                        ) : (
                            <div className={styles.value}>{formData.pager_number || 'N/A'}</div>
                        )}
                    </div>
                </div>

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label className={`${styles.label} ${styles.labelWithTooltip}`}>
                            Infotech Code / Code
                            <div title="The system will auto-generate a code. You can update it later with the actual Infotech code." className={styles.tooltipWrapper}>
                                <InfoIcon size={10} />
                            </div>
                        </label>
                        {isEditing ? (
                            <>
                                <input
                                    type="text"
                                    className={`${styles.input} ${errors.infotech_code || errors.code ? styles.inputError : ''}`}
                                    value={formData.infotech_code}
                                    onChange={(e) => handleChange('infotech_code', e.target.value)}
                                    placeholder="Enter infotech code"
                                />
                                {(errors.infotech_code || errors.code) && (
                                    <span className={styles.errorMessage}>
                                        {errors.infotech_code || errors.code}
                                    </span>
                                )}
                            </>
                        ) : (
                            <div className={styles.value}>{formData.infotech_code || 'N/A'}</div>
                        )}
                    </div>
                    {/* COMMENTED: Status toggle field - can be re-enabled in future if needed */}
                    {mode !== 'create' ? (
                        <div className={styles.formGroup}>
                            <label className={styles.label}>
                                Status*
                            </label>
                            <div className={styles.toggleContainer}>
                                <span className={styles.statusText}>
                                    {formData.status === 1 ? 'Active' : 'Inactive'}
                                </span>
                                <label className={styles.switch}>
                                    <input
                                        type="checkbox"
                                        checked={formData.status === 1}
                                        onChange={(e) => handleChange('status', e.target.checked ? 1 : 0)}
                                        disabled={!isEditing}
                                    />
                                    <span className={styles.slider}></span>
                                </label>
                            </div>
                            {errors.status && isEditing && (
                                <span className={styles.errorMessage}>{errors.status}</span>
                            )}
                        </div>
                    ) : (
                        <div className={styles.formGroup} />
                    )}
                </div>

                {isEditing && (
                    <div className={styles.buttonGroup}>
                        <button 
                            className={styles.cancelButton}
                            onClick={handleCancelClick}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button 
                            className={styles.saveButton}
                            onClick={handleSaveClick}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : (mode === 'create' ? 'Create' : 'Save')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddEditTherapist;

